/**
 * Visão do sócio — o escritório num relance. Aparece em Indicadores › Visão do sócio
 * (permissão "Ver a Visão do sócio"). Os blocos pessoais também são usados no Início. Tudo é clicável e leva à tela de origem.
 * Tendências: totais × fim do mês anterior; indicadores do mês × mesmo período do mês anterior.
 */
import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { navigate } from "@/lib/router";
import { addDaysIso, fmtMoney, STATE_LABEL, todayIso } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import { useCurrentUnit } from "@/lib/unit";
import { Empty, ErrorBox, Loading } from "@/ui/kit";
import { Avatar, Icon } from "@/ui/icons";

interface Trend { value: number; trend: number | null }
/** Blocos pessoais do Início (GET /painel/blocos). */
export interface BlocksData { today: string; month: string; calendar: Overview["calendar"]; agenda: Overview["agenda"]; tasks: Overview["tasks"] }
interface Overview {
  today: string; month: string; unitId: string | null; compareNote: string;
  kpis: { people: Trend; cases: Trend; processes: Trend; activities: Trend & { today: number; late: number } };
  byArea: Array<{ id: string; name: string; count: number }>;
  monthKpis: {
    closed: Trend; onTime: { value: number | null; delta: number | null };
    finance: null | { revenue: Trend; expenses: Trend };
  };
  agenda: Array<{ id: string; number: number; title: string; date: string; at: string | null; kind: "audiencia" | "reuniao" | "prazo" | "interna"; typeName: string | null; process: string | null; client: string | null }>;
  calendar: Record<string, { fatal: number; audiencia: number; outros: number }>;
  deadlines: Array<{ id: string; number: number; title: string; date: string; responsibleUserId: string | null; process: string | null; client: string | null }>;
  tasks: Array<{ id: string; number: number; title: string; state: string; due: string; hasDue: boolean; fatal: boolean }>;
  recent: {
    cases: Array<{ id: string; number: number; title: string; client: string | null; area: string; status: { label: string; color: string | null; final: boolean } }>;
    processes: Array<{ id: string; number: string; title: string | null; client: string | null; area: string; status: { label: string; color: string | null; final: boolean } }>;
    activities: Array<{ id: string; number: number; title: string; state: string; responsibleUserId: string | null; due: string | null }>;
  };
}

export const AREA_COLORS = ["#34506F", "#5E9C82", "#E0B04F", "#8E7CC3", "#E07B6A", "#5AA9C9", "#B08968", "#B9C0CA"];
const MES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const WD1 = ["D", "S", "T", "Q", "Q", "S", "S"];
const KIND: Record<Overview["agenda"][number]["kind"], { label: string; cls: string; icon: string }> = {
  reuniao: { label: "Reunião", cls: "tag-blue", icon: "user" },
  audiencia: { label: "Audiência", cls: "tag-violet", icon: "scale" },
  prazo: { label: "Prazo", cls: "tag-red", icon: "clock" },
  interna: { label: "Interna", cls: "tag-gray", icon: "briefcase" },
};
const TIPS = [
  "Use ⌘K (ou Ctrl+K) de qualquer tela para achar uma pessoa, caso, processo ou atividade.",
  "A unidade escolhida no menu lateral filtra Indicadores, Agenda (geral) e Atividades (todas e fila).",
  "Clique em qualquer número desta tela para abrir a lista de origem.",
  "Em Configurações › Cadastros jurídicos › Campos dos formulários o administrador define o que é obrigatório.",
  "Na tela do processo, a linha do tempo mostra fases, movimentos e prazos — tudo clicável.",
  "Os fluxos por tipo de ação (BPM / Workflow) criam as atividades de um caso automaticamente.",
  "Paginação: escolha 10, 25, 50 ou 100 por página — o sistema lembra a sua escolha.",
];

const longDate = (iso: string) => { const d = new Date(`${iso}T12:00:00Z`); return `${d.getUTCDate()} de ${MES[d.getUTCMonth()]} de ${d.getUTCFullYear()}`; };
const shortDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const hhmm = (iso: string) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

/** ↑ 12% (verde quando subir é bom; vermelho quando subir é ruim). */
export function TrendChip({ v, goodUp = true, unit = "%", title }: { v: number | null | undefined; goodUp?: boolean; unit?: string; title?: string }): JSX.Element | null {
  if (v === null || v === undefined) return <span className="trend flat" title={title ?? "Sem base de comparação no mês anterior"}>—</span>;
  if (v === 0) return <span className="trend flat" title={title}>= 0{unit}</span>;
  const up = v > 0;
  const good = up === goodUp;
  return <span className={`trend ${good ? "good" : "bad"}`} title={title}>{up ? "↑" : "↓"} {Math.abs(v).toLocaleString("pt-BR")}{unit}</span>;
}

function Kpi({ icon, tone, label, value, trend, hint, goodUp = true, onClick, note }: {
  icon: string; tone: string; label: string; value: number; trend: number | null; hint: string; goodUp?: boolean; onClick: () => void; note: string;
}): JSX.Element {
  return (
    <button className="kpi" onClick={onClick}>
      <div className="kpi-top"><span className={`bubble ${tone}`}><Icon name={icon} /></span><span>{label}</span></div>
      <div className="kpi-mid"><span className="kpi-val">{value.toLocaleString("pt-BR")}</span><TrendChip v={trend} goodUp={goodUp} title={note} /></div>
      <div className="kpi-hint">{hint}</div>
    </button>
  );
}

function Donut({ rows, total }: { rows: Array<{ name: string; count: number; color: string }>; total: number }): JSX.Element {
  const R = 52, C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <svg viewBox="0 0 140 140" width="150" height="150" role="img" aria-label="Distribuição por área">
      <circle cx="70" cy="70" r={R} fill="none" stroke="var(--border-l)" strokeWidth="22" />
      {total > 0 && rows.map((r) => {
        const len = (r.count / total) * C;
        const el = <circle key={r.name} cx="70" cy="70" r={R} fill="none" stroke={r.color} strokeWidth="22" strokeDasharray={`${Math.max(len - 1.5, 0.5)} ${C}`} strokeDashoffset={-acc} transform="rotate(-90 70 70)"><title>{`${r.name}: ${r.count}`}</title></circle>;
        acc += len;
        return el;
      })}
      <text x="70" y="62" textAnchor="middle" fontSize="10" fill="var(--muted)">Total</text>
      <text x="70" y="80" textAnchor="middle" fontSize="20" fontWeight="700" fill="var(--text)">{total}</text>
      <text x="70" y="94" textAnchor="middle" fontSize="9.5" fill="var(--muted)">processos</text>
    </svg>
  );
}

export function MiniCalendar({ month, today, marks, onMonth }: { month: string; today: string; marks: Overview["calendar"]; onMonth: (m: string) => void }): JSX.Element {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const first = new Date(Date.UTC(y, m - 1, 1));
  const start = addDaysIso(first.toISOString().slice(0, 10), -first.getUTCDay());
  const days = Array.from({ length: 42 }, (_, i) => addDaysIso(start, i));
  const weeks = days.slice(35).every((d) => d.slice(0, 7) !== month) ? days.slice(0, 35) : days;
  const shift = (n: number) => { const d = new Date(Date.UTC(y, m - 1 + n, 1)); onMonth(d.toISOString().slice(0, 7)); };
  return (
    <div className="mcal">
      <div className="mcal-head">
        <b>{MES[m - 1]!.replace(/^./, (c) => c.toUpperCase())} de {y}</b>
        <span className="row nowrap" style={{ gap: 2 }}>
          <button className="iconbtn sm" title="Mês anterior" onClick={() => shift(-1)}><Icon name="chevleft" size="sm" /></button>
          <button className="iconbtn sm" title="Próximo mês" onClick={() => shift(1)}><Icon name="chev" size="sm" /></button>
        </span>
      </div>
      <div className="mcal-grid">
        {WD1.map((w, i) => <span key={i} className="mcal-wd">{w}</span>)}
        {weeks.map((d) => {
          const mk = marks[d];
          const cls = ["mcal-day", d.slice(0, 7) !== month ? "other" : "", d === today ? "today" : ""].join(" ");
          const tip = mk ? [mk.fatal && `${mk.fatal} prazo(s) fatal(is)`, mk.audiencia && `${mk.audiencia} audiência(s)`, mk.outros && `${mk.outros} compromisso(s)`].filter(Boolean).join(" · ") : "";
          return (
            <button key={d} className={cls} title={tip || undefined} onClick={() => navigate(`/agenda?modo=dia&data=${d}`)}>
              {Number(d.slice(8))}
              {mk && <span className="mcal-dots">{mk.fatal > 0 && <i className="d-red" />}{mk.audiencia > 0 && <i className="d-violet" />}{mk.outros > 0 && <i className="d-gray" />}</span>}
            </button>
          );
        })}
      </div>
      <div className="mcal-legend"><span><i className="d-red" />prazo fatal</span><span><i className="d-violet" />audiência</span><span><i className="d-gray" />outros</span></div>
    </div>
  );
}

function dueLabel(iso: string, today: string): { text: string; cls: string } {
  if (iso < today) return { text: "Atrasada", cls: "tag-red" };
  if (iso === today) return { text: "Hoje", cls: "tag-red" };
  if (iso === addDaysIso(today, 1)) return { text: "Amanhã", cls: "tag-amber" };
  return { text: shortDate(iso), cls: "tag-gray" };
}

type AgendaItem = Overview["agenda"][number];
type TaskItem = Overview["tasks"][number];

/** Meus compromissos (hoje e próximos 7 dias), com etiqueta por tipo. */
export function AgendaCard({ agenda, today, title = "Meus próximos compromissos" }: { agenda: AgendaItem[]; today: string; title?: string }): JSX.Element {
  const days = [...new Set(agenda.map((a) => a.date))];
  return (
    <section className="card sv-agenda">
      <div className="card-head"><h2>{title}</h2><button className="link-btn" onClick={() => navigate("/agenda")}>Ver agenda completa →</button></div>
      {!agenda.length && <Empty icon="cal">Nenhum compromisso nos próximos 7 dias.</Empty>}
      {days.map((day) => (
        <div key={day}>
          <div className="sv-dayhead"><Icon name="cal" size="sm" />{day === today ? "Hoje, " : day === addDaysIso(today, 1) ? "Amanhã, " : ""}{longDate(day)}</div>
          {agenda.filter((a) => a.date === day).map((a) => (
            <button key={a.id} className="ag-item" onClick={() => navigate(`/atividades/${a.id}`)}>
              <span className="ag-time">{a.at ? hhmm(a.at) : "—"}</span>
              <span className={`ag-ic ${KIND[a.kind].cls}`}><Icon name={KIND[a.kind].icon} size="sm" /></span>
              <span className="ag-body">
                <span className="ag-title">{a.title}</span>
                <span className="ag-sub">{[a.client && `Cliente: ${a.client}`, a.process, a.typeName].filter(Boolean).join(" | ") || `#${a.number}`}</span>
              </span>
              <span className={`tag ${KIND[a.kind].cls}`}>{KIND[a.kind].label}</span>
              <Icon name="chev" size="sm" className="ag-chev" />
            </button>
          ))}
        </div>
      ))}
    </section>
  );
}

/** Minhas tarefas em aberto (Hoje / Amanhã / Atrasada). */
export function TasksCard({ tasks, today }: { tasks: TaskItem[]; today: string }): JSX.Element {
  return (
    <section className="card">
      <div className="card-head"><h2>Tarefas em aberto</h2><button className="link-btn" onClick={() => navigate("/atividades")}>Ver todas →</button></div>
      {!tasks.length && <Empty icon="tick">Nenhuma tarefa sua em aberto.</Empty>}
      {tasks.map((t) => {
        const dl = t.hasDue ? dueLabel(t.due, today) : null;
        return (
          <button key={t.id} className="task-row" onClick={() => navigate(`/atividades/${t.id}`)}>
            <span className={`task-dot ${t.state === "em_andamento" ? "doing" : t.state === "em_revisao" ? "review" : ""}`} title={STATE_LABEL[t.state] ?? t.state} />
            <span className="grow">{t.title}</span>
            {t.fatal && <span className="tag tag-red" title="Tem prazo fatal">fatal</span>}
            {dl && <span className={`tag ${dl.cls}`}>{dl.text}</span>}
          </button>
        );
      })}
    </section>
  );
}

/** Atalhos para os cadastros mais usados (conforme as permissões). */
export function QuickCard(): JSX.Element | null {
  const { can } = useLookups();
  const quick = [
    { icon: "user", label: "Novo cadastro", sub: "Pessoa", to: "/pessoas?novo=1", ok: can("pessoa.editar") },
    { icon: "folder", label: "Novo caso", to: "/casos?novo=1", ok: can("caso.editar") },
    { icon: "scale", label: "Novo processo", to: "/processos?novo=1", ok: can("processo.editar") },
    { icon: "check", label: "Registrar atividade", to: "/atividades?nova=1", ok: can("atividade.editar") },
    { icon: "doc", label: "Gerar documento", to: "/documentos", ok: can("documento.ver") },
    { icon: "cash", label: "Lançar despesa ou receita", to: "/financeiro?aba=titulos&novo=1", ok: can("financeiro.lancar") },
  ].filter((q) => q.ok);
  if (!quick.length) return null;
  return (
    <section className="card">
      <div className="card-head"><h2>Acesso rápido</h2></div>
      <div className="quick-grid">
        {quick.map((q) => (
          <button key={q.label} className="quick" onClick={() => navigate(q.to)}>
            <Icon name={q.icon} /><span>{q.label}{q.sub && <small>{q.sub}</small>}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

/** Dica do sistema (muda a cada dia). */
export function TipCard({ today }: { today: string }): JSX.Element {
  const tip = TIPS[Number(today.replace(/-/g, "")) % TIPS.length]!;
  return (
    <section className="tip-card">
      <div className="row nowrap" style={{ gap: 10, alignItems: "flex-start" }}>
        <Icon name="bulb" />
        <div className="tip-text"><b>Dica do sistema</b><div className="small" style={{ marginTop: 4 }}>{tip}</div></div>
      </div>
    </section>
  );
}

export function SocioView({ embedded }: { embedded?: boolean }): JSX.Element {
  const unitId = useCurrentUnit();
  const [month, setMonth] = useState<string | null>(null);
  const [recentTab, setRecentTab] = useState<"cases" | "processes" | "activities">("cases");
  const { can, userName } = useLookups();
  const { data, error, isLoading } = useQuery({
    queryKey: ["bi-socio", unitId, month],
    queryFn: () => api.get<Overview>(`/bi/socio${qs({ unitId: unitId || undefined, mes: month ?? undefined })}`),
    placeholderData: keepPreviousData, refetchInterval: 180_000,
  });
  if (isLoading) return <Loading lines={8} />;
  if (error || !data) return <ErrorBox error={error} />;
  const today = data.today;
  const k = data.kpis;
  const areaRows = data.byArea.slice(0, 7).map((a, i) => ({ ...a, color: AREA_COLORS[i % AREA_COLORS.length]! }));
  const rest = data.byArea.slice(7).reduce((s, a) => s + a.count, 0);
  if (rest) areaRows.push({ id: "_outros", name: "Outras", count: rest, color: AREA_COLORS[7]! });
  const areaTotal = data.byArea.reduce((s, a) => s + a.count, 0);
  const mk = data.monthKpis;
  const monthNote = "Do dia 1 até hoje, comparado com o mesmo período do mês anterior.";


  return (
    <div className={`sv ${embedded ? "embedded" : ""}`}>
      <div className="sv-main">
        <div className="kpi-row">
          <Kpi icon="user" tone="bb-blue" label="Pessoas" value={k.people.value} trend={k.people.trend} hint="total de clientes e contatos" note={data.compareNote} onClick={() => navigate("/pessoas")} />
          <Kpi icon="folder" tone="bb-amber" label="Casos" value={k.cases.value} trend={k.cases.trend} hint="em andamento" note={data.compareNote} onClick={() => navigate("/casos")} />
          <Kpi icon="scale" tone="bb-violet" label="Processos" value={k.processes.value} trend={k.processes.trend} hint="ativos" note={data.compareNote} onClick={() => navigate("/processos")} />
          <Kpi icon="check" tone="bb-green" label="Atividades" value={k.activities.value} trend={k.activities.trend} goodUp={false}
            hint={`${k.activities.today} para hoje · ${k.activities.late} atrasada${k.activities.late === 1 ? "" : "s"}`} note={data.compareNote} onClick={() => navigate("/atividades?visao=todas")} />
        </div>

        <div className="sv-row">
          <AgendaCard agenda={data.agenda} today={today} />

          <div className="sv-col">
            <section className="card">
              <div className="card-head"><h2>Distribuição por área</h2><button className="link-btn" onClick={() => navigate("/indicadores?aba=processos")}>Detalhar →</button></div>
              {areaTotal ? (
                <div className="donut-wrap">
                  <Donut rows={areaRows} total={areaTotal} />
                  <div className="donut-legend">
                    {areaRows.map((r) => (
                      <button key={r.id} className="dl-row" onClick={() => r.id !== "_outros" && navigate(`/processos?area=${r.id}`)}>
                        <i style={{ background: r.color }} /><span className="grow">{r.name}</span><b>{Math.round((r.count / areaTotal) * 100)}%</b>
                      </button>
                    ))}
                  </div>
                </div>
              ) : <Empty icon="chart">Nenhum processo ativo.</Empty>}
            </section>
            <section className="card">
              <div className="card-head"><h2><Icon name="chart" size="sm" /> Indicadores do mês</h2></div>
              <div className="mk-grid">
                <button className="mk" onClick={() => navigate("/indicadores?aba=processos&periodo=mes")} title={monthNote}>
                  <span className="mk-l">Processos concluídos</span>
                  <span className="mk-v">{mk.closed.value}<TrendChip v={mk.closed.trend} title={monthNote} /></span>
                </button>
                <button className="mk" onClick={() => navigate("/indicadores?aba=equipe&periodo=mes")} title={monthNote}>
                  <span className="mk-l">Prazo em dia</span>
                  <span className="mk-v">{mk.onTime.value === null ? "—" : `${mk.onTime.value}%`}<TrendChip v={mk.onTime.delta} unit=" p.p." title={monthNote} /></span>
                </button>
                {mk.finance ? <>
                  <button className="mk" onClick={() => navigate("/financeiro?aba=fluxo")} title={monthNote}>
                    <span className="mk-l">Receita do mês</span>
                    <span className="mk-v">{fmtMoney(mk.finance.revenue.value)}<TrendChip v={mk.finance.revenue.trend} title={monthNote} /></span>
                  </button>
                  <button className="mk" onClick={() => navigate("/financeiro?aba=fluxo")} title={monthNote}>
                    <span className="mk-l">Despesas do mês</span>
                    <span className="mk-v">{fmtMoney(mk.finance.expenses.value)}<TrendChip v={mk.finance.expenses.trend} goodUp={false} title={monthNote} /></span>
                  </button>
                </> : <div className="muted small mk-note">Receita e despesa aparecem para quem tem acesso de gerente financeiro.</div>}
              </div>
            </section>
          </div>
        </div>

        <div className="sv-row">
          <section className="card">
            <div className="seg-tabs">
              {([["cases", "Casos recentes"], ["processes", "Processos"], ["activities", "Atividades"]] as const).map(([key, label]) => (
                <button key={key} className={recentTab === key ? "on" : ""} onClick={() => setRecentTab(key)}>{label}</button>
              ))}
            </div>
            <div className="table-wrap">
              {recentTab === "cases" && (
                <table className="table compact">
                  <thead><tr><th>Caso</th><th>Cliente</th><th>Área</th><th>Status</th></tr></thead>
                  <tbody>{data.recent.cases.map((c) => (
                    <tr key={c.id} className="clickable" onClick={() => navigate(`/casos/${c.id}`)}>
                      <td><b>{c.number}</b> <span className="muted small">{c.title}</span></td><td>{c.client ?? "—"}</td><td>{c.area}</td>
                      <td><span className="tag" style={c.status.color ? { color: c.status.color, background: `${c.status.color}1A` } : undefined}>{c.status.label}</span></td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
              {recentTab === "processes" && (
                <table className="table compact">
                  <thead><tr><th>Processo</th><th>Cliente</th><th>Área</th><th>Etapa</th></tr></thead>
                  <tbody>{data.recent.processes.map((p) => (
                    <tr key={p.id} className="clickable" onClick={() => navigate(`/processos/${p.id}`)}>
                      <td className="mono">{p.number}</td><td>{p.client ?? "—"}</td><td>{p.area}</td>
                      <td><span className="tag" style={p.status.color ? { color: p.status.color, background: `${p.status.color}1A` } : undefined}>{p.status.label}</span></td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
              {recentTab === "activities" && (
                <table className="table compact">
                  <thead><tr><th>Atividade</th><th>Responsável</th><th>Prazo</th><th>Situação</th></tr></thead>
                  <tbody>{data.recent.activities.map((a) => (
                    <tr key={a.id} className="clickable" onClick={() => navigate(`/atividades/${a.id}`)}>
                      <td><b>#{a.number}</b> {a.title}</td><td>{userName(a.responsibleUserId)}</td><td>{a.due ? shortDate(a.due) : "—"}</td>
                      <td><span className={`tag ${a.state === "concluida" ? "tag-green" : a.state === "em_revisao" ? "tag-violet" : "tag-blue"}`}>{STATE_LABEL[a.state] ?? a.state}</span></td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
            <button className="link-btn small" style={{ marginTop: 8 }} onClick={() => navigate(recentTab === "cases" ? "/casos" : recentTab === "processes" ? "/processos" : "/atividades?visao=todas")}>
              Ver todos →
            </button>
          </section>

          <TasksCard tasks={data.tasks} today={today} />
        </div>
      </div>

      <aside className="sv-side">
        <section className="card">
          <MiniCalendar month={data.month} today={today} marks={data.calendar} onMonth={setMonth} />
        </section>
        <section className="card">
          <div className="card-head"><h2>Próximos prazos</h2><button className="link-btn" onClick={() => navigate("/agenda")}>Ver todos →</button></div>
          {!data.deadlines.length && <Empty icon="tick">Sem prazos fatais pela frente.</Empty>}
          {data.deadlines.map((d) => (
            <button key={d.id} className="dl-item" onClick={() => navigate(`/atividades/${d.id}`)}>
              <span className={`dl-date ${d.date <= addDaysIso(today, 2) ? "hot" : ""}`}>{shortDate(d.date)}</span>
              <span className="grow">
                <span className="dl-title">{d.title}</span>
                <span className="dl-sub">{[d.process, d.client].filter(Boolean).join(" · ") || `#${d.number}`}</span>
              </span>
              {d.responsibleUserId && <Avatar name={userName(d.responsibleUserId)} id={d.responsibleUserId} size={22} />}
            </button>
          ))}
        </section>
        <QuickCard />
        <TipCard today={today} />
      </aside>
    </div>
  );
}
